import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, X, MapPin, Calendar, Building2, ExternalLink, RefreshCw } from 'lucide-react';
import { PAPER_BASE } from './config';
import PaperSEO from '../../components/paper/PaperSEO';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const formatPostDate = (d) => {
  if (!d || d.length !== 8) return '';
  return `${d.slice(0, 4)}.${d.slice(4, 6)}.${d.slice(6, 8)}`;
};

const formatDateRange = (start, end) => {
  if (!start) return '';
  if (!end || end === start) return start;
  return `${start} ~ ${end}`;
};

export default function PaperWorkDetailPage() {
  const { id } = useParams();
  const [work, setWork] = useState(null);
  const [prev, setPrev] = useState(null);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsExpanded, setReviewsExpanded] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/paperWork/${id}`)
      .then(res => {
        setWork(res.data.work);
        setPrev(res.data.prev);
        setNext(res.data.next);
        const imgs = [];
        if (res.data.work.images) {
          res.data.work.images
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .forEach(img => imgs.push({ url: img.imageUrl, caption: img.caption || '' }));
        }
        setLightboxImages(imgs);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    // 블로그 리뷰 로드
    setReviewsLoading(true);
    axios.get(`${API}/api/paperWork/${id}/reviews`)
      .then(res => setReviews(res.data.reviews || []))
      .catch(() => {})
      .finally(() => setReviewsLoading(false));
  }, [id]);

  const refreshReviews = useCallback(() => {
    setReviewsLoading(true);
    const token = localStorage.getItem('token');
    axios.post(`${API}/api/paperWork/${id}/reviews/refresh`, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(res => setReviews(res.data.reviews || []))
      .catch(() => {})
      .finally(() => setReviewsLoading(false));
  }, [id]);

  const navigateLightbox = useCallback((dir) => {
    if (lightbox === null) return;
    const nextIdx = lightbox + dir;
    if (nextIdx >= 0 && nextIdx < lightboxImages.length) {
      setLightbox(nextIdx);
    }
  }, [lightbox, lightboxImages]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, navigateLightbox]);

  // Back link based on category
  const getBackLink = () => {
    if (work?.category === 'FESTIVAL') return `${PAPER_BASE}/festival`;
    return `${PAPER_BASE}/event`;
  };

  const getBackLabel = () => {
    if (work?.category === 'FESTIVAL') return '축제 목록';
    return '공연 목록';
  };

  if (loading) {
    return (
      <div className="bg-[#0d0b14] min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (!work) {
    return (
      <div className="bg-[#0d0b14] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/40 mb-4">작품을 찾을 수 없습니다.</p>
          <Link to={`${PAPER_BASE}/show`} className="text-violet-400 text-sm hover:underline">목록으로 돌아가기</Link>
        </div>
      </div>
    );
  }

  const posterImage = work.posterUrl || work.thumbnailUrl;
  const galleryImages = work.images?.sort((a, b) => a.sortOrder - b.sortOrder) || [];

  const plainContent = work.content
    ? work.content.replace(/<[^>]*>/g, '').slice(0, 120)
    : '';

  const eventJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: work.title,
    ...(work.workDate && { startDate: work.workDate }),
    ...(work.workDateEnd && { endDate: work.workDateEnd }),
    ...(work.location && {
      location: { '@type': 'Place', name: work.location },
    }),
    ...(posterImage && { image: posterImage }),
    organizer: {
      '@type': 'Organization',
      name: '핑크페이퍼',
      url: 'https://pinkpaper.kr',
    },
  };

  return (
    <div className="bg-[#0d0b14] min-h-screen">
      <PaperSEO
        title={`${work.title} | 핑크페이퍼`}
        description={plainContent || `${work.title} - 핑크페이퍼 프로젝트`}
        path={`/work/${work.id}`}
        image={posterImage}
        jsonLd={eventJsonLd}
      />
      {/* Background Hero Section */}
      <section className="relative overflow-hidden">
        {/* Blurred background */}
        {posterImage && (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center scale-110"
              style={{ backgroundImage: `url(${posterImage})`, filter: 'blur(40px)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d0b14] via-[#0d0b14]/80 to-[#0d0b14]/40" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0d0b14] via-[#0d0b14]/60 to-transparent" />
          </>
        )}

        {/* Content */}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-12 sm:pb-16">
          {/* Back Link */}
          <Link to={getBackLink()} className="inline-flex items-center gap-2 text-white/40 text-sm hover:text-violet-400 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            <span className="tracking-[0.1em]">{getBackLabel()}</span>
          </Link>

          <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8 md:gap-12">
            {/* Poster */}
            {posterImage && (
              <div className="w-[140px] sm:w-[260px] md:w-[320px] shrink-0 mx-auto sm:mx-0">
                <div className="aspect-[210/297] rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={posterImage}
                    alt={work.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Info */}
            <div className="flex-1 min-w-0 pt-2 sm:pt-4">
              {/* Category Badge */}
              {work.category && (
                <span className="inline-flex items-center px-3 py-1 bg-violet-500/10 border border-violet-500/20 rounded-full text-violet-400 text-xs font-medium mb-4">
                  {work.category === 'FESTIVAL' ? '축제' : work.category === 'EVENT' ? '공연' : work.category}
                </span>
              )}

              {/* Title */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                {work.title}
              </h1>

              {/* Tagline */}
              {work.tagline && (
                <p className="text-white/50 text-sm mb-6">{work.tagline}</p>
              )}

              {/* Meta */}
              <div className="space-y-3 mb-6">
                {work.workDate && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 text-violet-400/60 shrink-0" />
                    <span className="text-white/50 text-sm">{formatDateRange(work.workDate, work.workDateEnd)}</span>
                  </div>
                )}
                {work.location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-violet-400/60 shrink-0" />
                    <span className="text-white/50 text-sm">{work.location}</span>
                  </div>
                )}
                {work.clientName && (
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-violet-400/60 shrink-0" />
                    <span className="text-white/50 text-sm">{work.clientName}</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {work.content && (
                <div
                  className="prose prose-invert prose-sm max-w-none text-white/50 leading-relaxed whitespace-pre-line"
                  dangerouslySetInnerHTML={{ __html: work.content }}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Video */}
      {work.youtubeUrl && (() => {
        const match = work.youtubeUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([^?&/]+)/);
        const videoId = match?.[1];
        return videoId ? (
          <section className="py-10">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <h3 className="text-white/50 text-xs tracking-[0.2em] uppercase mb-6">Video</h3>
              <div className="aspect-video rounded-xl overflow-hidden bg-[#1a1428]">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title={work.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </section>
        ) : null;
      })()}

      {/* Image Gallery */}
      {galleryImages.length > 0 && (
        <section className="pt-10 pb-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h3 className="text-white/50 text-xs tracking-[0.2em] uppercase mb-8">Gallery</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {galleryImages.map((img, idx) => (
                <div key={img.id} className="group">
                  <div
                    onClick={() => setLightbox(idx)}
                    className="aspect-[4/3] rounded-xl overflow-hidden bg-[#1a1428] cursor-pointer"
                  >
                    <img
                      src={img.imageUrl}
                      alt={`${work.title} ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  {img.caption && (
                    <p className="text-white/70 text-xs mt-2 px-2.5 py-1.5 bg-white/5 rounded-lg line-clamp-1">{img.caption}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Reviews */}
      <section className="pt-10 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-white/50 text-xs tracking-[0.2em] uppercase">Review</h3>
            {localStorage.getItem('token') && (
              <button
                onClick={refreshReviews}
                disabled={reviewsLoading}
                className="flex items-center gap-1.5 text-white/30 hover:text-violet-400 text-xs transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${reviewsLoading ? 'animate-spin' : ''}`} />
                <span>새로고침</span>
              </button>
            )}
          </div>

          {reviewsLoading && reviews.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin" />
            </div>
          ) : reviews.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(reviewsExpanded ? reviews : reviews.slice(0, 6)).map((review) => (
                  <a
                    key={review.id}
                    href={review.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 hover:border-violet-500/20 rounded-xl p-5 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h4 className="text-white/80 text-sm font-medium line-clamp-2 group-hover:text-violet-300 transition-colors leading-relaxed">
                        {review.title}
                      </h4>
                      <ExternalLink className="w-4 h-4 text-white/20 group-hover:text-violet-400 shrink-0 mt-0.5 transition-colors" />
                    </div>
                    <p className="text-white/35 text-xs leading-relaxed line-clamp-2 mb-3">
                      {review.description}
                    </p>
                    <div className="flex items-center gap-2 text-white/25 text-[11px]">
                      <span className="inline-flex items-center px-1.5 py-0.5 bg-green-500/10 text-green-400/70 rounded text-[10px] font-medium">BLOG</span>
                      <span>{review.bloggerName}</span>
                      {review.postDate && (
                        <>
                          <span className="text-white/10">|</span>
                          <span>{formatPostDate(review.postDate)}</span>
                        </>
                      )}
                    </div>
                  </a>
                ))}
              </div>
              {reviews.length > 6 && !reviewsExpanded && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => setReviewsExpanded(true)}
                    className="text-white/30 hover:text-violet-400 text-xs tracking-wider transition-colors"
                  >
                    + {reviews.length - 6}개 더보기
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-white/20 text-sm text-center py-8">관련 리뷰를 찾지 못했습니다.</p>
          )}
        </div>
      </section>

      {/* Prev / Next Navigation */}
      <section className="pb-32 border-t border-white/5 pt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex justify-between">
          {prev ? (
            <Link to={`${PAPER_BASE}/work/${prev.id}`} className="group flex items-center gap-3 text-white/40 hover:text-violet-400 transition-colors">
              <ChevronLeft className="w-5 h-5" />
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase mb-1">Previous</p>
                <p className="text-sm font-light">{prev.title}</p>
              </div>
            </Link>
          ) : <div />}
          {next ? (
            <Link to={`${PAPER_BASE}/work/${next.id}`} className="group flex items-center gap-3 text-white/40 hover:text-violet-400 transition-colors text-right">
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase mb-1">Next</p>
                <p className="text-sm font-light">{next.title}</p>
              </div>
              <ChevronRight className="w-5 h-5" />
            </Link>
          ) : <div />}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && lightboxImages[lightbox] && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center"
          onClick={() => setLightbox(null)}
        >
          <button className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white transition-colors z-10" onClick={() => setLightbox(null)}>
            <X className="w-6 h-6" />
          </button>
          {lightbox > 0 && (
            <button className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/30 hover:text-white transition-colors z-10" onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}>
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          {lightbox < lightboxImages.length - 1 && (
            <button className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/30 hover:text-white transition-colors z-10" onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}>
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
          <div className="max-w-5xl max-h-[85vh] px-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative inline-block">
              <img src={lightboxImages[lightbox].url} alt={work.title} className="max-w-full max-h-[80vh] object-contain mx-auto rounded-lg" />
              {lightboxImages[lightbox].caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm rounded-b-lg px-4 py-3">
                  <p className="text-white/90 text-sm text-center">{lightboxImages[lightbox].caption}</p>
                </div>
              )}
            </div>
            <p className="text-white/30 text-xs mt-3 text-center">{lightbox + 1} / {lightboxImages.length}</p>
          </div>
        </div>
      )}
    </div>
  );
}
